import BaseLoading from '@components/BaseLoading';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError } from '@components/toast';
import { appConfig } from '@configs/index';
import { cameraViewController } from '@controllers/index';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import { CameraViewType, PatrolTimeForecast, ViewPreset, ViewPTZ } from '@EcamModel/model/CameraView';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { Filter } from '@pages/cameras/list';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { search } from '@pages/cameras/list/helpers';
import color from '@theme/Colors';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCameraDetailContext } from '../../../../hook/CameraDetailsProvider';
import GroupViewCameraPtzDnd from './components/GroupViewCameraPtzDnd';
import UpsertView360 from './components/UpsertView360';
import ViewCameraPTZ from './components/ViewCameraPTZ';
import { checkUnmappedPreset } from './helpers';
import LoadingTextSkeleton from '@components/skeleton/LoadingTextSkeleton';
import { buildPatrolSummary } from './configs';
import BaseTableGeoZonesDetailPTZ from '../components/BaseTableGeoZonesDetailPTZ';
import { GeoZoneType } from '@EcamModel/model/GeoZones';
import PopUpWarning from '@components/PopUpWarning';
import ScrollableBoxCustom from '@pages/verification/pc-details/components/ScrollableBoxCustom';

export const cameraViewBlob = appConfig.gateway.cameraViewBlobURL;

export enum ModeUpsertViewPtz {
    UpsertOverview,
}

export default function GeoZonesOverViewPTZ() {
    const {
        listCameraView,
        filteredCamera,
        isLoading,
        selectedCameraView,
        backdrop,
        setListCameraView,
        handleDndViewOverview,
        setSelectedCameraView,
        selectedGeozone,
        patrolTimeForecast,
        isLoadingPatrolTimeForecast,
        isFetchingPatrolTimeForecast,
        popUpDeleteGeoZonesOverview,
        setSelectedGeozone,
        handleDeleteGeoZonesOverView,
        handleDeleteGeoZonesDetail,
        isLoadingSave,
        handleResetStyledMenu,
        camera,
    } = useCameraDetailContext();

    const location = useLocation();

    const cameraViewId: number | undefined = location?.state?.cameraViewId;

    useEffect(() => {
        if (filteredCamera && Object.keys(filteredCamera).length > 0) {
            getListPreset(filteredCamera);
        }
    }, [filteredCamera]);

    const hasInitialized = useRef(false);

    const [loadingPreset, setLoadingPreset] = useState<boolean>(true);
    const [listsViewPreset, setListsViewPreset] = useState<ViewPreset[]>([]);
    const [listsViewPresetFull, setListsViewPresetFull] = useState<ViewPreset[]>([]);
    const [filter, setFilter] = useState<Filter>({
        page: 1,
        perspectiveView: listsViewPreset?.[0],
    });
    const [listCollapse, setListCollapse] = useState<{ [key: string]: boolean }>({});
    const [mode, setMode] = useState<ModeUpsertViewPtz>();
    const [viewPTZ, setViewPTZ] = useState<ViewPTZ & {}>();

    const rightPaneRef = useRef<HTMLDivElement | null>(null);
    const [rightPaneHeight, setRightPaneHeight] = useState<number>(0);

    const getListPreset = async (_filter: ISendActionGet) => {
        setLoadingPreset(true);
        await cameraViewController
            .getListPreset(_filter)
            .then((res) => {
                filterUnmappedPreset(res);
                setListsViewPresetFull(res);
            })
            .catch((err) => {
                pushError(err.response?.data?.message || 'Not found list preset.');
            })
            .finally(() => {
                setLoadingPreset(false);
            });
    };

    const filterUnmappedPreset = (presets: ViewPreset[]) => {
        const unmapped: ViewPreset[] = [];

        for (const preset of presets) {
            const mapping = checkUnmappedPreset(preset.GotoPointId, preset.PresetName, listCameraView.rows);

            if (!mapping) {
                unmapped.push(preset);
            }
        }

        setListsViewPreset(unmapped);
    };

    const filteredPresets =
        selectedCameraView?.ViewType === CameraViewType.ViewDetail
            ? listsViewPreset.filter((preset) => {
                  const name = preset.PresetName?.toLowerCase() || '';
                  return !['overview', 'over view'].some((keyword) => name.includes(keyword));
              })
            : listsViewPreset;

    const listsViewPresetAutocomplete = useStyledAutocomplete({
        list: {
            options: filteredPresets,
            isFiltered: (option, searchText) => search(option.PresetName ?? '', searchText),
        },
        dependencyList: [listsViewPreset],
        loading: loadingPreset,
    });

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) return;

        const oldIndex = listCameraView.rows.findIndex((item) => item.Id === active.id);
        const newIndex = listCameraView.rows.findIndex((item) => item.Id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        backdrop.setTrue();

        const newRows = arrayMove(listCameraView.rows, oldIndex, newIndex);

        setListCameraView((prev) => ({
            ...prev,
            rows: newRows,
        }));

        await handleDndViewOverview(newRows).finally(() => backdrop.setFalse());
    };

    const handleClick = (id: string) => {
        setListCollapse((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const handleConfirmDeleteGeoZones = () => {
        const deleteHandlers = {
            [GeoZoneType.Overview]: handleDeleteGeoZonesOverView,
            [GeoZoneType.Detail]: handleDeleteGeoZonesDetail,
        };
        const deleteHandler = deleteHandlers[selectedGeozone?.GeoZoneType!];
        if (deleteHandler) {
            selectedGeozone?.Id && deleteHandler(selectedGeozone.Id.toString());
        }
    };

    useEffect(() => {
        if (
            !hasInitialized.current &&
            selectedGeozone?.CameraViewId &&
            cameraViewId &&
            selectedCameraView?.ViewType === CameraViewType.ViewDetail
        ) {
            const matchedView = listCameraView?.rows?.find((e) => e?.Id === selectedGeozone.CameraViewId);

            if (matchedView) {
                setListCollapse((prev) => ({
                    ...prev,
                    [`${matchedView.Id}`]: true,
                }));
            }

            hasInitialized.current = true;
        }
    }, [selectedGeozone?.CameraViewId, listCameraView]);

    useEffect(() => {
        if (listsViewPresetFull.length) filterUnmappedPreset(listsViewPresetFull);
    }, [listCameraView.rows]);

    useEffect(() => {
        if (!rightPaneRef.current) return;

        const element = rightPaneRef.current;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const newHeight = Math.round(entry.contentRect.height);
                if (newHeight !== rightPaneHeight) setRightPaneHeight(newHeight);
            }
        });

        resizeObserver.observe(element);
        setRightPaneHeight(Math.round(element.getBoundingClientRect().height));

        return () => resizeObserver.disconnect();
    }, [rightPaneRef.current]);

    return (
        <Stack p={1} spacing={2}>
            <Stack
                direction={'row'}
                justifyContent={'space-between'}
                alignItems={'center'}
                sx={{
                    pointerEvents: mode !== undefined ? 'none' : 'auto',
                }}
            >
                <Box>
                    <Typography variant="h5">Patrol sequence</Typography>
                    <LoadingTextSkeleton
                        conditions={isLoadingPatrolTimeForecast || isFetchingPatrolTimeForecast}
                        value={buildPatrolSummary(patrolTimeForecast ?? ({} as PatrolTimeForecast))}
                        loadingStyle={{
                            width: '100%',
                        }}
                        textStyle={{
                            color: color.grey600,
                        }}
                    />
                </Box>
                <Button
                    variant="contained"
                    disabled={loadingPreset || listsViewPreset.length == 0}
                    onClick={() => {
                        setSelectedCameraView(undefined);
                        setMode(ModeUpsertViewPtz.UpsertOverview);
                    }}
                >
                    Add overview
                </Button>
            </Stack>

            <Divider orientation="horizontal" sx={{ borderBottomWidth: 1, borderColor: color.grey300, mx: 2 }} />

            {isLoading ? (
                <BaseLoading displayHeight={'300px'} />
            ) : listCameraView?.rows?.length > 0 || mode === ModeUpsertViewPtz.UpsertOverview ? (
                <Stack direction={'row'} spacing={2}>
                    <Stack
                        width={{ xs: '40%' }}
                        borderRadius={2}
                        bgcolor={color.grey100}
                        p={2}
                        spacing={2}
                        sx={{
                            maxHeight: rightPaneHeight ? `${rightPaneHeight}px` : 'none',
                            overflow: 'auto',
                        }}
                    >
                        <ScrollableBoxCustom>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                <SortableContext
                                    items={listCameraView.rows.map((item) => item.Id!)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {listCameraView?.rows?.map((e, i) => (
                                        <GroupViewCameraPtzDnd
                                            key={e.Id}
                                            id={e.Id!}
                                            cameraView={e}
                                            collapse={() => handleClick(`${e.Id}`)}
                                            listCollapse={listCollapse}
                                            isDisabled={mode !== undefined}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </ScrollableBoxCustom>
                    </Stack>
                    <Box padding={2} borderRadius={2} bgcolor={color.grey100} flex={1} width={'60%'} ref={rightPaneRef}>
                        {mode === ModeUpsertViewPtz.UpsertOverview ? (
                            <UpsertView360
                                filter={filter}
                                onSetFilter={setFilter}
                                listsViewPreset={listsViewPreset}
                                listsViewPresetAutocomplete={listsViewPresetAutocomplete}
                                onCancel={() => {
                                    setViewPTZ(undefined);
                                    setMode(undefined);
                                }}
                                viewPtz={viewPTZ}
                            />
                        ) : (
                            <ViewCameraPTZ
                                configView={() => {
                                    setViewPTZ({
                                        PtzPanPos: selectedCameraView?.PtzPanPos ?? '',
                                        PtzTiltPos: selectedCameraView?.PtzTiltPos ?? '',
                                        PtzZoomPos: selectedCameraView?.PtzZoomPos ?? '',
                                    });

                                    setMode(ModeUpsertViewPtz.UpsertOverview);
                                }}
                                listsViewPreset={listsViewPreset}
                            />
                        )}
                    </Box>
                </Stack>
            ) : (
                <ListNoData />
            )}

            <Stack direction={'column'} spacing={2} mt={2} p={2} bgcolor={color.white}>
                <Typography variant="h5" color={color.darkPrimary}>
                    View: {selectedCameraView?.CameraViewName} - Geo-zones details (
                    {selectedCameraView?.GeoZones?.filter((gz) => gz.GeoZoneType === GeoZoneType.Detail).length ?? 0})
                </Typography>

                <Divider orientation="horizontal" flexItem sx={{ borderColor: color.grey300 }} />

                <BaseTableGeoZonesDetailPTZ
                    rows={selectedCameraView?.GeoZones ?? []}
                    camera={camera}
                    handleDelete={(item) => {
                        setSelectedGeozone(item);
                        popUpDeleteGeoZonesOverview.setTrue();
                    }}
                    onUpdate={(updatedGeoZone) => {
                        if (selectedCameraView) {
                            const updatedGeoZones =
                                selectedCameraView.GeoZones?.map((geoZone) =>
                                    geoZone.Id === updatedGeoZone.Id ? updatedGeoZone : geoZone
                                ) ?? [];

                            setSelectedCameraView((prev) => ({
                                ...prev!,
                                GeoZones: updatedGeoZones,
                            }));
                        }
                    }}
                />
            </Stack>

            <PopUpWarning
                {...popUpDeleteGeoZonesOverview}
                onConfirm={handleConfirmDeleteGeoZones}
                title="Confirm"
                message={
                    selectedGeozone?.GeoZoneType === GeoZoneType.Overview
                        ? 'Are you sure you want to delete this geo-zone overview? This action will cause all the geo-zones within it to be deleted?'
                        : 'Are you sure you want to delete this geo-zone?'
                }
                isLoading={isLoadingSave}
                onClose={() => {
                    handleResetStyledMenu();
                    popUpDeleteGeoZonesOverview.onClose();
                }}
            />
        </Stack>
    );
}
